import type { ReactNode } from "react";

import {
	Box,
	Chip,
	ClickAwayListener,
	Icon,
	IconButton,
	Paper,
	Popper,
	type Theme,
	ThemeProvider
} from "@mui/material";
import { useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import BackgroundJobQueueButton from "~/components/backgroundJobs/BackgroundJobQueueButton";
import { MOBILE_NAV_MIN_TOUCH_TARGET_PX } from "~/components/mobile/bottomNav/mobileBottomNav";
import MobileFernieLauncher from "~/components/mobile/MobileFernieLauncher";
import {
	MOBILE_TOP_BAR_HEIGHT,
	MOBILE_TOP_BAR_OFFSET_VAR,
	MOBILE_TOP_BAR_ZINDEX,
	mobileTopBarOffset,
	mobileTopBarTop
} from "~/components/mobile/mobileChrome";
import NotificationBell from "~/components/NotificationBell";
import { TitleText } from "~/components/Typographies";
import ApplicationsMenu from "~/components/worksuite/ApplicationsMenu";
import Config from "~/config";
import AccountMenu from "~/layout/AccountMenu";
import InboxButton from "~/layout/InboxButton";
import MobileSearchLauncher from "~/layout/mobile/MobileSearchLauncher";
import MobileStickySubHeader from "~/layout/mobile/MobileStickySubHeader";
import StatusBarScrim from "~/layout/mobile/StatusBarScrim";
import AppThemeContext from "~/providers/AppThemeContext";
import isBenchmarkHost from "~/pwa/benchmarkHost";
import { findAppForPath } from "~/pwa/installableApps";
import { isAndroid } from "~/pwa/openApp";
import useStandaloneApp from "~/pwa/useStandaloneApp";
import { themeCreator } from "~/themes/pageThemes";

const ICON_TILE_SIZE = 28;

const ICON_TILE_TAP_INSET_PX = (MOBILE_NAV_MIN_TOUCH_TARGET_PX - ICON_TILE_SIZE) / 2;

const PHONE_TOUCH_ROW_SX = (theme: Theme) => ({
	[theme.breakpoints.down("md")]: {
		"& .MuiIconButton-root": { minHeight: MOBILE_NAV_MIN_TOUCH_TARGET_PX, minWidth: MOBILE_NAV_MIN_TOUCH_TARGET_PX },
		gap: 0
	}
});

const PHONE_TILE_FACE_SX = (theme: Theme) => ({
	[theme.breakpoints.down("md")]: {
		backgroundClip: "content-box",
		borderRadius: `calc(${theme.shape.borderRadius}px * 1.5 + ${ICON_TILE_TAP_INSET_PX}px)`,
		height: MOBILE_NAV_MIN_TOUCH_TARGET_PX,
		p: `${ICON_TILE_TAP_INSET_PX}px`,
		width: MOBILE_NAV_MIN_TOUCH_TARGET_PX
	}
});

let mountedTopBarCount = 0;

interface TileThemeScopeProps {
	children: ReactNode;
	theme: Theme | null;
}

const TileThemeScope = ({ children, theme }: TileThemeScopeProps) =>
	theme ? <ThemeProvider theme={theme}>{children}</ThemeProvider> : <>{children}</>;

export interface MobileTopBarProps {
	appIcon?: ReactNode;
	identifier?: string;
	subHeader?: ReactNode;
	title: string;
}

const MobileTopBar = ({ appIcon, identifier, subHeader, title }: MobileTopBarProps) => {
	const location = useLocation();
	const { isConfined } = useStandaloneApp();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);

	useEffect(() => setAnchorEl(null), [location.pathname]);

	useLayoutEffect(() => {
		mountedTopBarCount += 1;
		document.documentElement.style.setProperty(MOBILE_TOP_BAR_OFFSET_VAR, mobileTopBarOffset);
		return () => {
			mountedTopBarCount -= 1;
			if (mountedTopBarCount === 0) document.documentElement.style.removeProperty(MOBILE_TOP_BAR_OFFSET_VAR);
		};
	}, []);

	const { themeType } = useContext(AppThemeContext);
	const routeApp = findAppForPath(location.pathname);
	const routeAppKey = routeApp?.key;
	const tileTheme = useMemo(
		() => (routeAppKey ? themeCreator(routeAppKey, themeType) : null),
		[routeAppKey, themeType]
	);
	const isNonProd = Config.environment !== "production";
	const titleText = identifier ? `${title} / ${identifier}` : title;
	const glyph = appIcon ?? (routeApp ? <Icon sx={{ fontSize: 20 }}>{routeApp.icon}</Icon> : null);
	const isOnBenchmarkHost = isBenchmarkHost(window.location.host);
	const canSwitchApps = !isOnBenchmarkHost && (!isConfined || isAndroid());

	return (
		<>
			<StatusBarScrim />
			<Box
				sx={[
					{
						alignItems: "center",
						bgcolor: "background.paper",
						display: "flex",
						gap: 1,
						height: MOBILE_TOP_BAR_HEIGHT,
						position: "sticky",
						px: 1,
						top: mobileTopBarTop,
						zIndex: MOBILE_TOP_BAR_ZINDEX
					},
					PHONE_TOUCH_ROW_SX
				]}
			>
				<Box sx={[{ alignItems: "center", display: "flex", gap: 1, minWidth: 0 }, PHONE_TOUCH_ROW_SX]}>
					{glyph && (
						<TileThemeScope theme={appIcon ? null : tileTheme}>
							<IconButton
								aria-expanded={canSwitchApps ? open : undefined}
								aria-haspopup={canSwitchApps ? "menu" : undefined}
								aria-label={canSwitchApps ? "open app switcher" : "current app"}
								disabled={!canSwitchApps}
								onClick={canSwitchApps ? (event) => setAnchorEl(open ? null : event.currentTarget) : undefined}
								size="small"
								sx={[
									appIcon
										? {
												"& > img, & > svg": {
													display: "block",
													height: "100%",
													width: "100%"
												},
												flexShrink: 0,
												height: ICON_TILE_SIZE,
												p: 0,
												width: ICON_TILE_SIZE
											}
										: {
												"&:hover": {
													backgroundColor: "primary.main",
													filter: "brightness(0.9)"
												},
												backgroundColor: "primary.main",
												borderRadius: 1.5,
												color: "primary.contrastText",
												flexShrink: 0,
												height: ICON_TILE_SIZE,
												p: 0,
												width: ICON_TILE_SIZE
											},
									PHONE_TILE_FACE_SX
								]}
							>
								{glyph}
							</IconButton>
						</TileThemeScope>
					)}
					<TitleText bold noWrap sx={{ minWidth: 0 }}>
						{titleText}
					</TitleText>
				</Box>
				<Box sx={{ flex: 1 }} />
				{!isConfined && <MobileSearchLauncher />}
				{(!isConfined || isOnBenchmarkHost) && <MobileFernieLauncher />}
				{isNonProd && (
					<Chip color="warning" label={Config.environment} size="small" sx={{ flexShrink: 0 }} variant="outlined" />
				)}
				<InboxButton />
				<BackgroundJobQueueButton />
				<NotificationBell />
				<AccountMenu />
			</Box>
			<Popper
				anchorEl={anchorEl}
				open={open && canSwitchApps}
				placement="bottom-start"
				sx={{ zIndex: (theme) => theme.zIndex.modal }}
			>
				<ClickAwayListener onClickAway={() => setAnchorEl(null)}>
					<Paper
						sx={{
							maxHeight: "calc(100dvh - 96px)",
							maxWidth: 440,
							minWidth: 200,
							overflowY: "auto",
							p: 1.5,
							width: "calc(100vw - 32px)"
						}}
						variant="outlined"
					>
						<ApplicationsMenu />
					</Paper>
				</ClickAwayListener>
			</Popper>
			{subHeader && <MobileStickySubHeader>{subHeader}</MobileStickySubHeader>}
		</>
	);
};

export default MobileTopBar;
